import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CalendarRange, Download, MapPin, RotateCcw, Route as RouteIcon, Search } from 'lucide-react';
import { deviceDisplayName, totalDistanceMeters } from '@mytag/shared';
import type { LocationPoint } from '@mytag/shared';
import { useDevices, useLocations } from '@/hooks/queries';
import { PageHeader } from '@/components/layout/AppShell';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  EmptyState,
  Field,
  Input,
  LoadingState,
  Select,
  StatTile,
  Table,
  Td,
  Th,
} from '@/components/ui/primitives';
import { ErrorState } from '@/components/common/ErrorState';
import { BatteryGauge, CopyButton, LocationTypeBadge } from '@/components/common/indicators';
import { OpenInMapsButton } from '@/components/common/OpenInMapsButton';
import { MapView } from '@/components/map/MapView';
import { mytagApi } from '@/services/mytagApi';
import {
  formatDateTime,
  formatMeters,
  formatSpeed,
  fromDateTimeInput,
  toDateTimeInput,
} from '@/lib/format';

const DEFAULT_WINDOW_HOURS = 24;

export function HistoryPage(): JSX.Element {
  const { deviceNum: routeDeviceNum } = useParams<{ deviceNum: string }>();
  const navigate = useNavigate();
  const devicesQuery = useDevices();

  const [selected, setSelected] = useState(routeDeviceNum ?? '');
  const [fromInput, setFromInput] = useState(() =>
    toDateTimeInput(new Date(Date.now() - DEFAULT_WINDOW_HOURS * 3600_000).toISOString()),
  );
  const [toInput, setToInput] = useState(() => toDateTimeInput(new Date().toISOString()));
  const [applied, setApplied] = useState<{ from?: string; to?: string }>(() => ({
    from: new Date(Date.now() - DEFAULT_WINDOW_HOURS * 3600_000).toISOString(),
    to: new Date().toISOString(),
  }));
  // Point choisi par clic sur la carte pour « Ouvrir dans Maps ». null = pas de choix explicite.
  const [selectedPoint, setSelectedPoint] = useState<LocationPoint | null>(null);

  // Sélection par défaut : device de la route, sinon premier device disponible.
  useEffect(() => {
    if (selected) return;
    const first = routeDeviceNum ?? devicesQuery.data?.devices[0]?.deviceNum;
    if (first) setSelected(first);
  }, [routeDeviceNum, devicesQuery.data, selected]);

  // Un nouveau device ou une nouvelle période invalide la sélection précédente sur la carte.
  useEffect(() => {
    setSelectedPoint(null);
  }, [selected, applied.from, applied.to]);

  const history = useLocations(selected || undefined, { ...applied, limit: 500 });
  const points = useMemo(() => history.data?.points ?? [], [history.data]);
  // Dernière capture chronologique (marqueur rouge « Arrivée ») : jamais le premier point (vert, « Départ »).
  const lastPoint = points.length > 0 ? points[points.length - 1] ?? null : null;
  const mapTargetPoint = selectedPoint ?? lastPoint;
  const selectedDevice = devicesQuery.data?.devices.find((device) => device.deviceNum === selected);
  const mapTargetLabel = selectedDevice ? deviceDisplayName(selectedDevice) : selected || 'SmartTag';

  const stats = useMemo(() => {
    if (points.length === 0) return null;
    const first = points[0];
    const last = points[points.length - 1];
    return {
      count: points.length,
      distance: totalDistanceMeters(points),
      first: first?.timestamp ?? null,
      last: last?.timestamp ?? null,
    };
  }, [points]);

  const csv = useMemo(() => {
    const header = 'captureTimestamp,publishedAt,latitude,longitude,accuracy,battery,locationType,speed';
    const rows = points.map((point) =>
      [
        point.timestamp,
        point.datePublished ?? '',
        point.latitude,
        point.longitude,
        point.accuracy ?? '',
        point.battery ?? '',
        point.locationType,
        point.speed ?? '',
      ].join(','),
    );
    return [header, ...rows].join('\n');
  }, [points]);

  function applyFilters(): void {
    setApplied({ from: fromInput ? fromDateTimeInput(fromInput) : undefined, to: fromDateTimeInput(toInput) });
  }

  function resetFilters(): void {
    const from = new Date(Date.now() - DEFAULT_WINDOW_HOURS * 3600_000).toISOString();
    const to = new Date().toISOString();
    setFromInput(toDateTimeInput(from));
    setToInput(toDateTimeInput(to));
    setApplied({ from, to });
  }
  function preset(hours: number): void { const to=new Date().toISOString(), from=new Date(Date.now()-hours*3600_000).toISOString(); setFromInput(toDateTimeInput(from)); setToInput(toDateTimeInput(to)); setApplied({from,to}); }

  if (devicesQuery.isLoading) return <LoadingState label="Chargement des devices…" />;
  if (devicesQuery.isError) {
    return <ErrorState error={devicesQuery.error} onRetry={() => void devicesQuery.refetch()} />;
  }

  const devices = devicesQuery.data?.devices ?? [];

  if (devices.length === 0) {
    return (
      <>
        <PageHeader title="Historique des positions" />
        <Card>
          <EmptyState
            icon={<RouteIcon className="h-5 w-5" />}
            title="Aucun device lié"
            description="Lier un SmartTag avant de consulter un historique."
            action={<Button onClick={() => navigate('/devices')}>Aller aux devices</Button>}
          />
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Historique des positions"
        description="Filtrer par période, visualiser le trajet et la liste chronologique."
        actions={
          points.length > 0 ? <><CopyButton value={csv} label="Copier en CSV" size="md" /><Button variant="outline" icon={<Download className="h-4 w-4"/>} onClick={()=>void mytagApi.downloadHistory('csv',{deviceNum:selected,from:applied.from!,to:applied.to!})}>CSV</Button><Button variant="outline" icon={<Download className="h-4 w-4"/>} onClick={()=>void mytagApi.downloadHistory('pdf',{deviceNum:selected,from:applied.from!,to:applied.to!})}>PDF</Button></> : undefined
        }
      />

      <Alert tone="info" title="Deux horodatages fournisseur distincts">
        « Heure de capture » utilise le champ <code>timestamp</code> de la position. « Publication
        fournisseur » utilise <code>datePublished</code> et n’est jamais présentée comme l’heure de
        localisation ; plusieurs captures peuvent donc partager la même publication.
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarRange className="h-4 w-4 text-muted-foreground" aria-hidden />
            Filtres
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 lg:grid-cols-[minmax(0,1.4fr)_1fr_1fr_auto_auto] lg:items-end">
            <Field label="Device" htmlFor="device-select">
              <Select
                id="device-select"
                value={selected}
                onChange={(event) => {
                  setSelected(event.target.value);
                  navigate(`/devices/${event.target.value}/history`, { replace: true });
                }}
              >
                {devices.map((device) => (
                  <option key={device.deviceNum} value={device.deviceNum}>
                    {deviceDisplayName(device)} — {device.deviceNum}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Du" htmlFor="from">
              <Input
                id="from"
                type="datetime-local"
                value={fromInput}
                onChange={(event) => setFromInput(event.target.value)}
              />
            </Field>
            <Field label="Au" htmlFor="to">
              <Input
                id="to"
                type="datetime-local"
                value={toInput}
                onChange={(event) => setToInput(event.target.value)}
              />
            </Field>
            <Button icon={<Search className="h-4 w-4" />} onClick={applyFilters} loading={history.isFetching}>
              Appliquer
            </Button>
            <Button variant="outline" icon={<RotateCcw className="h-4 w-4" />} onClick={resetFilters}>
              24 h
            </Button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2"><Button size="sm" variant="ghost" onClick={()=>preset(24)}>24 dernières heures</Button><Button size="sm" variant="ghost" onClick={()=>preset(24*7)}>7 derniers jours</Button><Button size="sm" variant="ghost" onClick={()=>{const d=new Date();d.setHours(0,0,0,0);const to=new Date().toISOString();setFromInput(toDateTimeInput(d.toISOString()));setToInput(toDateTimeInput(to));setApplied({from:d.toISOString(),to});}}>Aujourd’hui</Button></div>
        </CardContent>
      </Card>

      {history.isError ? (
        <ErrorState error={history.error} onRetry={() => void history.refetch()} title="Historique indisponible" />
      ) : null}

      {history.isLoading ? <LoadingState label="Chargement de l'historique…" /> : null}

      {!history.isLoading && points.length === 0 && !history.isError ? (
        <Card>
          <EmptyState
            icon={<MapPin className="h-5 w-5" />}
            title="Aucune position sur cette période"
            description="Élargir la plage de dates, ou rafraîchir la position du device depuis sa fiche."
            action={
              <Button variant="outline" icon={<RotateCcw className="h-4 w-4" />} onClick={resetFilters}>
                Réinitialiser sur 24 h
              </Button>
            }
          />
        </Card>
      ) : null}

      {stats ? (
        <>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <StatTile label="Points" value={stats.count} icon={<MapPin className="h-4 w-4" />} tone="info" />
            <StatTile
              label="Distance cumulée"
              value={formatMeters(stats.distance)}
              icon={<RouteIcon className="h-4 w-4" />}
              tone="success"
            />
            <StatTile label="Première capture" value={formatDateTime(stats.first)} />
            <StatTile label="Dernière capture" value={formatDateTime(stats.last)} />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Trajet</CardTitle>
            </CardHeader>
            <CardContent>
              <MapView
                points={points}
                showRoute
                height="440px"
                selectedPoint={selectedPoint}
                onSelectPoint={setSelectedPoint}
              />
            </CardContent>
            <CardFooter className="items-center justify-between">
              <OpenInMapsButton point={mapTargetPoint} label={mapTargetLabel} />
              <span className="text-xs text-muted-foreground">
                {selectedPoint
                  ? `Point sélectionné sur la carte : ${formatDateTime(selectedPoint.timestamp)}`
                  : lastPoint
                    ? `Dernière capture : ${formatDateTime(lastPoint.timestamp)}`
                    : null}
              </span>
            </CardFooter>
          </Card>

          <Card>
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle>Liste chronologique</CardTitle>
              <span className="text-xs text-muted-foreground">
                <Download className="mr-1 inline h-3 w-3" aria-hidden />
                {stats.count} entrée(s)
              </span>
            </CardHeader>
            <CardContent className="p-0 sm:p-0">
              <Table>
                <thead>
                  <tr>
                    <Th>#</Th>
                    <Th>Heure de capture</Th>
                    <Th>Latitude</Th>
                    <Th>Longitude</Th>
                    <Th>Précision</Th>
                    <Th>Source</Th>
                  <Th>Batterie</Th>
                  <Th>Publication fournisseur</Th>
                    <Th>Vitesse</Th>
                  </tr>
                </thead>
                <tbody>
                  {[...points].reverse().map((point, index) => (
                    <tr key={`${point.timestamp}-${index}`} className="hover:bg-muted/40">
                      <Td className="tabular text-xs text-muted-foreground">{points.length - index}</Td>
                      <Td className="whitespace-nowrap text-xs">{formatDateTime(point.timestamp)}</Td>
                      <Td className="tabular font-mono text-xs">{point.latitude.toFixed(6)}</Td>
                      <Td className="tabular font-mono text-xs">{point.longitude.toFixed(6)}</Td>
                      <Td className="text-xs">{formatMeters(point.accuracy)}</Td>
                      <Td>
                        <LocationTypeBadge type={point.locationType} />
                      </Td>
                  <Td>
                    <BatteryGauge level={point.battery} />
                  </Td>
                  <Td className="text-xs">{formatDateTime(point.datePublished ?? null)}</Td>
                      <Td className="text-xs">{formatSpeed(point.speed)}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </CardContent>
          </Card>
        </>
      ) : null}
    </>
  );
}
